import styled from '@emotion/styled';
import { css } from '@emotion/react';
import COLORS_BABY from '../../styles/emotion/colorsBaby';
import COLORS, { COLORS_SEMANTIC } from '../../styles/emotion/colors3';
import { COLORS as COLORS_OLD, FONT, SPACING } from '../../styles/emotion';

export enum TagV2Variant {
  /** A Tag applied on top of a photo: white background */
  WHITE = 'white',

  /** Informational, e.g. “Best Seller,” “Upgrade,” “Zola x Draper James.”.  gray background */
  GRAY = 'gray',

  /** This tag indicates success or a positive state. green background. */
  GREEN = 'green',

  /** This tag indicates an error or required action. red background  */
  RED = 'red',

  PURPLE = 'purple',

  SKY = 'sky',

  GOLD = 'gold',
  SILVER = 'silver',
  BRONZE = 'bronze',
  WINE = 'wine',
  COVE = 'cove',
  BAY = 'bay',
  FOREST = 'forest',

  BLACK_010 = 'black-010',

  NEON_LIME = 'neon-lime',

  /** @deprecated - not in the design system */
  ORANGE = 'orange',

  /** @deprecated - not in the design system, here for compatibility with tag v1 */
  LIGHT_NAVY = 'lightNavy',

  /** @deprecated - not in the design system, here for compatibility with tag v1 */
  ALTERNATE = 'alternate',

  /** @deprecated - not in the design system, here for compatibility with tag v1 */
  PRIMARY = 'primary',

  /** @deprecated - not in the design system, here for compatibility with tag v1 */
  SECONDARY = 'secondary',
}

export enum TagV2Size {
  REGULAR = 'sizeRegular',
  LARGE = 'sizeLarge',
  SMALL = 'sizeSmall',
}

const TAG_COLOR_MAP: Record<TagV2Variant, string> = {
  [TagV2Variant.WHITE]: COLORS.BLACK_100,
  [TagV2Variant.PURPLE]: COLORS.WINE_100,
  [TagV2Variant.GRAY]: COLORS.BLACK_100,
  [TagV2Variant.GREEN]: COLORS.FOREST_100,
  [TagV2Variant.RED]: COLORS.ROSE_100,
  [TagV2Variant.SKY]: COLORS.MARINE_100,
  [TagV2Variant.GOLD]: COLORS.BLACK_100,
  [TagV2Variant.SILVER]: COLORS.BLACK_100,
  [TagV2Variant.BLACK_010]: COLORS.BLACK_100,
  [TagV2Variant.BRONZE]: COLORS.BLACK_100,
  [TagV2Variant.WINE]: COLORS.LILAC_100,
  [TagV2Variant.COVE]: COLORS.WHITE_100,
  [TagV2Variant.BAY]: COLORS.WHITE_100,
  [TagV2Variant.FOREST]: COLORS.WHITE_100,
  [TagV2Variant.NEON_LIME]: COLORS_BABY.TAN_080,

  //  deprecated
  [TagV2Variant.ORANGE]: COLORS.WHITE_100,
  [TagV2Variant.ALTERNATE]: COLORS.WHITE_100,
  [TagV2Variant.PRIMARY]: COLORS.FOREST_100,
  [TagV2Variant.LIGHT_NAVY]: COLORS.WHITE_100,
  [TagV2Variant.SECONDARY]: COLORS.FOREST_100,
};

const TAG_BACKGROUND_COLOR_MAP: Record<TagV2Variant, string> = {
  [TagV2Variant.WHITE]: COLORS.WHITE_100,
  [TagV2Variant.PURPLE]: COLORS.LILAC_100,
  [TagV2Variant.GRAY]: COLORS.BLACK_005,
  [TagV2Variant.GREEN]: COLORS.MINT_100,
  [TagV2Variant.RED]: COLORS.BLUSH_100,
  [TagV2Variant.SKY]: COLORS.SKY_100,
  [TagV2Variant.GOLD]: '#FFCA0C',
  [TagV2Variant.SILVER]: '#DED9CF',
  [TagV2Variant.BLACK_010]: COLORS.BLACK_010,
  [TagV2Variant.BRONZE]: '#F5A75B',
  [TagV2Variant.WINE]: COLORS.WINE_100,
  [TagV2Variant.COVE]: COLORS.COVE_100,
  [TagV2Variant.BAY]: COLORS.BAY_100,
  [TagV2Variant.FOREST]: COLORS.FOREST_100,
  [TagV2Variant.NEON_LIME]: COLORS_BABY.NEON_LIME_100,

  //  deprecated
  [TagV2Variant.ORANGE]: COLORS_SEMANTIC.WARNING_100,
  [TagV2Variant.ALTERNATE]: COLORS_OLD.BOULDER,
  [TagV2Variant.PRIMARY]: COLORS.MINT_100,
  [TagV2Variant.LIGHT_NAVY]: '#336081',
  [TagV2Variant.SECONDARY]: COLORS.WHITE_100,
};

export const TagEmoji = styled.span`
  && {
    font-size: ${({ size }: { size: TagV2Size }) =>
      size === TagV2Size.SMALL ? FONT.FONT_SIZE_EXTRA_SMALL : FONT.FONT_SIZE_SMALLER};
    margin-right: ${({ size }: { size: TagV2Size }) => (size === TagV2Size.SMALL ? '4px' : '7px')};
    font-family: Arial, Helvetica;
  }
`;

export const TagContainer = styled.div<{
  variant: TagV2Variant;
  size: TagV2Size;
  emoji?: {
    symbol: string;
    alt?: string;
  };
}>`
  ${({ size }) =>
    `font-weight: ${
      size === TagV2Size.LARGE ? FONT.FONT_WEIGHT_SEMI_BOLD : FONT.FONT_WEIGHT_REGULAR
    };`}
  font-size: ${FONT.FONT_SIZE_EXTRA_SMALL};
  border-radius: 4px;
  padding-left: ${SPACING.XS};
  padding-right: ${SPACING.XS};
  height: ${SPACING.MD};
  display: inline-block;
  /* variant determines background */
  ${({ variant }) => `background-color: ${TAG_BACKGROUND_COLOR_MAP[variant]};`}
  /* use white text on non-white backgrounds */
  ${({ variant }) => `color: ${TAG_COLOR_MAP[variant]};`}
  ${({ variant }) => {
    /* give dropshadow and black text for white variant */
    if (variant === TagV2Variant.WHITE) {
      return css`
        color: ${COLORS.BLACK_100};
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      `;
    }
    /* give special border and text color to secondary variant */
    if (variant === TagV2Variant.SECONDARY) {
      return css`
        border: 1px solid ${COLORS.FOREST_100};
      `;
    }
    return '';
  }}
  /* size prop determines size and font-size of tag */
  ${({ size, emoji }) => {
    if (size === TagV2Size.SMALL) {
      return css`
        font-size: ${FONT.FONT_SIZE_EXTRA_SMALL};
        min-height: initial;
        min-width: initial;
        height: initial;
        padding-left: 6px;
        padding-right: 6px;
        padding-bottom: 2px;
        padding-top: 2px;
        line-height: 16px;

        ${TagEmoji} {
          font-size: ${FONT.FONT_SIZE_EXTRA_SMALL};
          margin-right: ${SPACING.XXS};
        }
      `;
    }
    if (size === TagV2Size.LARGE || typeof emoji !== 'undefined') {
      return css`
        height: 24px;
        font-size: ${FONT.FONT_SIZE_SMALLER};
      `;
    }
    // else
    return css`
      height: 20px;
    `;
  }}
`;

export const TagContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

export const TagImage = styled.img`
  margin-right: 6px;
`;

export const TagList = styled.div`
  display: flex;
  ${TagContainer} {
    margin-right: ${SPACING.XS};
    &:last-child {
      margin-right: 0;
    }
  }
`;
